import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, useParams, useRoutes } from 'react-router-dom';
import { useAuth } from './providers/AuthProvider';
import { GetConveyorsMenu } from './redux/reducers/conveyorsMenu/thunks';
import { useAppDispatch } from './redux/store';

const AccessDenied = lazy(() => import('./pages/AccessDenied'));
const Attendances = lazy(() => import('./pages/Attendances'));
const AuthenticationActivity = lazy(() => import('./pages/AuthenticationActivity/AuthenticationActivity'));
const UserActivity = lazy(() => import('./pages/UserActivity'));
const OnlineUsers = lazy(() => import('./pages/OnlineUsersPage'));
const BoxPressedProcess = lazy(() => import('./pages/BoxPressedProcess/BoxPressedProcess'));
const CigarPerformance = lazy(() => import('./pages/CigarPerformance'));
const CigarPrices = lazy(() => import('./pages/CigarPrices'));
const CigarProductionPlans = lazy(() => import('./pages/CigarProductionPlans'));
const CigarProductionPlanSummaries = lazy(() => import('./pages/CigarProductionPlanSummaries'));
const EmployeeTasksPage = lazy(() => import('./pages/EmployeeTasksPage'));
const TechnicalTasksPage = lazy(() => import('./pages/TechnicalTasksPage'));

// ----------------------------------------------------------------------
const CigarUnitBoxProduction = lazy(() =>
  import('./pages/CigarUnitBoxProduction/CigarUnitBoxProduction').then((module) => ({
    default: module.CigarUnitBoxProduction,
  }))
);
const CigarUnitProduction = lazy(() => import('./pages/CigarUnitProduction'));
const AttributionLogs = lazy(() => import('./pages/AttributionLogs'));
const ProductionAttributionDebug = lazy(() => import('./pages/ProductionAttributionDebug'));
const CigarUnitsPage = lazy(() => import('./pages/CigarUnitsPage'));
const CigarUnitsPeriodEndPage = lazy(() => import('./pages/CigarUnitsPeriodEnd'));
const Cigars = lazy(() => import('./pages/Cigars'));
const ConveyorCigarUnitProductions = lazy(() => import('./pages/ConveyorCigarUnitProductions'));
const ConveyorStepRecords = lazy(() => import('./pages/ConveyorStepRecords'));
const ConveyorStepRecordsHistory = lazy(() => import('./pages/ConveyorStepRecordsHistory'));
const ConveyorTemplates = lazy(() => import('./pages/ConveyorTemplates'));
const ConveyorTransactionsPage = lazy(() => import('./pages/ConveyorTransactionsPage'));
const CycleCountPage = lazy(() => import('./pages/CycleCountPage'));
const DashboardApp = lazy(() => import('./pages/DashboardApp'));
const DashboardLayout = lazy(() => import('./layouts/dashboard'));
const DepartmentOrder = lazy(() => import('./pages/DepartmentOrder'));
const DepartmentsPage = lazy(() => import('./pages/DepartmentsPage'));
const Distributors = lazy(() => import('./pages/Distributors'));
const EmployeeTeams = lazy(() => import('./pages/EmployeeTeams'));
const EmployeeUnitProductions = lazy(() => import('./pages/EmployeeUnitProductions'));
const Employees = lazy(() => import('./pages/Employees'));
const ExpensesPage = lazy(() => import('./pages/ExpensesPage'));
const Factories = lazy(() => import('./pages/Factories'));
const FactoryOptionsPage = lazy(() => import('./pages/FactoryOptionsPage'));
const FactoryFilesPage = lazy(() => import('./pages/FactoryFilesPage'));
const FinishedGoodInventory = lazy(() => import('./pages/FinishedGoodInventory'));
const FinishedGoodTemplates = lazy(() => import('./pages/FinishedGoodTemplates'));
const GiftSummaryPage = lazy(() => import('./pages/GiftSummaryPage'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Login = lazy(() => import('./pages/Login'));
const LogoOnlyLayout = lazy(() => import('./layouts/LogoOnlyLayout'));
const NotFound = lazy(() => import('./pages/Page404'));
const PackingLists = lazy(() => import('./pages/PackingLists'));
const PagePermissions = lazy(() => import('./pages/PagePermissions'));
const PageInProgress = lazy(() => import('./pages/PageInProgress'));
const PaymentCodeCigarMappingPage = lazy(() => import('./pages/PaymentCodeCigarMappingPage'));
const Payrolls = lazy(() => import('./pages/payrolls'));
const PayrollAddPage = lazy(() => import('./pages/payrolls/PayrollAddPage'));
const PayrollsEdit = lazy(() => import('./pages/payrolls/edit'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));
const PurchaseOrder = lazy(() => import('./pages/PurchaseOrder'));
const QrCodes = lazy(() => import('./pages/QrCodes'));
const RawMaterialInventory = lazy(() => import('./pages/RawMaterialInventory/RawMaterialInventory'));
const RoomsPage = lazy(() => import('./pages/RoomsPage'));
const SchedulerTasks = lazy(() => import('./pages/SchedulerTasksPage'));
const PairCigarScheduling = lazy(() => import('./pages/PairCigarSchedulingPage'));
const NotificationTemplates = lazy(() => import('./pages/NotificationTemplates'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const TobaccoClassificationAnalysisPage = lazy(() => import('./pages/TobaccoClassificationAnalysisPage'));
const TobaccoClassificationSummaryPage = lazy(() => import('./pages/TobaccoClassificationSummaryPage'));
const TobaccoContainersPage = lazy(() => import('./pages/TobaccoContainersPage'));
const TobaccoContainersPeriodEndPage = lazy(() => import('./pages/TobaccoContainersPeriodEndPage'));
const TobaccoCorrectionsPage = lazy(() => import('./pages/TobaccoCorrectionsPage'));
const TobaccoDryRoomProductionPage = lazy(() => import('./pages/TobaccoDryRoomProductionPage'));
const TobaccoGaleraProductionPage = lazy(() => import('./pages/TobaccoGaleraProductionPage'));
const TobaccoHumidityRoomProductionPage = lazy(() => import('./pages/TobaccoHumidityRoomProductionPage'));
const TobaccoInventory = lazy(() => import('./pages/TobaccoInventory'));
const TobaccoMappingPage = lazy(() => import('./pages/TobaccoMappingPage/TobaccoMappingsPage'));
const TobaccoProduction = lazy(() => import('./pages/TobaccoProduction'));
const TobaccoSalesPage = lazy(() => import('./pages/TobaccoSalesPage'));
const TobaccoTransactionsPage = lazy(() => import('./pages/TobaccoTransactionsPage'));
const TobaccoTransferSummaryPage = lazy(() => import('./pages/TobaccoTransferSummaryPage'));
const TobaccoWorkOrdersPage = lazy(() => import('./pages/TobaccoWorkOrdersPage'));
const TobaccosPage = lazy(() => import('./pages/Tobaccos/TobaccosPage'));
const Taxes = lazy(() => import('./pages/Taxes'));
const UnitByQR = lazy(() => import('./pages/UnitByQR'));
const UserNotifications = lazy(() => import('./pages/UserNotifications'));
const Users = lazy(() => import('./pages/Users'));
const UsersWithoutFactory = lazy(() => import('./pages/UsersWithoutFactory/UsersWithoutFactory'));
const WorkInProgressInventory = lazy(() => import('./pages/WorkInProgressInventory/WorkInProgressInventory'));
const WorkInProgressInventoryHistory = lazy(
  () => import('./pages/WorkInProgressInventoryHistory/WorkInProgressInventoryHistory')
);
const WorkOrders = lazy(() => import('./pages/WorkOrders'));

// ----------------------------------------------------------------------

const withProtectedRoute = (path, Element) => (
  <Suspense fallback={<CircularProgress />}>
    <ProtectedRoute path={path}>
      <Element />
    </ProtectedRoute>
  </Suspense>
);

const withSuspense = (Element) => (
  <Suspense fallback={<CircularProgress />}>
    <Element />
  </Suspense>
);

const WithParamsKey = ({ Component }: { Component: React.ComponentType }) => {
  const { id } = useParams();
  return <Component key={id} />;
};

const withParamsProtectedRoute = (path, Element) => (
  <Suspense fallback={<CircularProgress />}>
    <ProtectedRoute path={path}>
      <WithParamsKey Component={Element} />
    </ProtectedRoute>
  </Suspense>
);

export default function RouterWrapper() {
  const { onGetMe, isAuthentificated, loading } = useAuth();
  const dispatch = useAppDispatch();

  useEffect(() => {
    onGetMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthentificated) {
      dispatch(GetConveyorsMenu());
    }
  }, [dispatch, isAuthentificated]);

  if (loading) {
    return (
      <Box sx={{ width: '100%', height: '100%' }} display={'flex'} alignItems={'center'} justifyContent={'center'}>
        <CircularProgress disableShrink />
      </Box>
    );
  }

  return <Router isAuthentificated={isAuthentificated} />;
}

function Router({ isAuthentificated }) {
  return useRoutes([
    {
      path: '/:factory/unit/:qrId',
      element: withSuspense(UnitByQR),
    },
    {
      path: '/conveyor-transactions',
      element: isAuthentificated ? withSuspense(DashboardLayout) : <Navigate to="/login" />,
      children: [
        {
          path: ':id',
          element: withParamsProtectedRoute('conveyorTransactions', ConveyorTransactionsPage),
        },
      ],
    },
    {
      path: '/dashboard',
      element: isAuthentificated ? withSuspense(DashboardLayout) : <Navigate to="/login" />,
      children: [
        {
          path: 'app',
          element: withSuspense(DashboardApp),
        },
        {
          path: 'user-notifications',
          element: withSuspense(UserNotifications),
        },
        {
          path: 'factories',
          element: withProtectedRoute('factories', Factories),
        },
        {
          path: 'authentication-activity',
          element: withProtectedRoute('authenticationActivity', AuthenticationActivity),
        },
        {
          path: 'user-activity',
          element: withProtectedRoute('userActivity', UserActivity),
        },
        {
          path: 'online-users',
          element: withProtectedRoute('onlineUsers', OnlineUsers),
        },
        {
          path: 'users-without-factory',
          element: withProtectedRoute('usersWithoutFactory', UsersWithoutFactory),
        },
        {
          path: 'page-permissions',
          element: withProtectedRoute('pagePermissions', PagePermissions),
        },
        {
          path: 'notification-templates',
          element: withProtectedRoute('notificationTemplates', NotificationTemplates),
        },

        {
          path: 'users',
          element: withProtectedRoute('users', Users),
        },
        {
          path: 'cigars',
          element: withProtectedRoute('cigars', Cigars),
        },
        {
          path: 'conveyors',
          element: withProtectedRoute('conveyors', ConveyorTemplates),
        },
        /* {
          path: 'conveyors-flow',
          element: withProtectedRoute('conveyors-flow', ConveyorsFlowPage),
        }, */
        {
          path: 'tobacco-classification-summary',
          element: withProtectedRoute('tobaccoClassificationSummary', TobaccoClassificationSummaryPage),
        },
        {
          path: 'tobacco-classification-analysis',
          element: withProtectedRoute('tobaccoClassificationAnalysis', TobaccoClassificationAnalysisPage),
        },
        {
          path: 'tobacco-containers-period-end',
          element: withProtectedRoute('tobaccoContainersPeriodEnd', TobaccoContainersPeriodEndPage),
        },
        {
          path: 'tobacco-corrections',
          element: withProtectedRoute('tobaccoCorrections', TobaccoCorrectionsPage),
        },
        {
          path: 'tobacco-work-orders',
          element: withProtectedRoute('tobaccoWorkOrders', TobaccoWorkOrdersPage),
        },
        {
          path: 'attribution-logs',
          element: withProtectedRoute('attributionLogs', AttributionLogs),
        },
        {
          path: 'production-attribution-debug',
          element: withProtectedRoute('factories', ProductionAttributionDebug), // Using 'factories' permission temporarily or if you want a specific one
        },
        {
          path: 'cigar-prices',
          element: withProtectedRoute('cigarPrices', CigarPrices),
        },
        {
          path: 'tobaccos',
          element: withProtectedRoute('tobaccos', TobaccosPage),
        },
        {
          path: 'rooms',
          element: withProtectedRoute('rooms', RoomsPage),
        },
        {
          path: 'distributors',
          element: withProtectedRoute('distributors', Distributors),
        },
        {
          path: 'departments',
          element: withProtectedRoute('departments', DepartmentsPage),
        },
        {
          path: 'factory-options',
          element: withProtectedRoute('factoryOptions', FactoryOptionsPage),
        },
        {
          path: 'factory-files',
          element: withProtectedRoute('factoryOptions', FactoryFilesPage),
        },
        {
          path: 'taxes',
          element: withProtectedRoute('taxes', Taxes),
        },
        {
          path: 'employees',
          element: withProtectedRoute('employees', Employees),
        },
        {
          path: 'employee-teams',
          element: withProtectedRoute('employeeTeams', EmployeeTeams),
        },
        {
          path: 'attendances',
          element: withProtectedRoute('attendances', Attendances),
        },
        {
          path: 'cigar-performance',
          element: withProtectedRoute('cigarPerformance', CigarPerformance),
        },
        {
          path: 'cigar-production-plans',
          element: withProtectedRoute('cigarProductionPlans', CigarProductionPlans),
        },
        {
          path: 'cigar-production-plan-summaries',
          element: withProtectedRoute('cigarProductionPlans', CigarProductionPlanSummaries),
        },
        {
          path: 'employee-tasks',
          element: withProtectedRoute('employeeTasks', EmployeeTasksPage),
        },
        {
          path: 'technical-tasks',
          element: withProtectedRoute('employeeTasks', TechnicalTasksPage),
        },
        {
          path: 'employeeUnitProductions',
          element: withProtectedRoute('employeeUnitProductions', EmployeeUnitProductions),
        },
        {
          path: 'conveyor-step-records',
          element: withProtectedRoute('conveyorStepRecords', ConveyorStepRecords),
        },
        {
          path: 'conveyor-step-records-history',
          element: withProtectedRoute('conveyorStepRecordsHistory', ConveyorStepRecordsHistory),
        },
        {
          path: 'tobacco-inventory',
          element: withProtectedRoute('tobaccoInventory', TobaccoInventory),
        },
        {
          path: 'tobacco-containers',
          element: withProtectedRoute('tobaccoContainers', TobaccoContainersPage),
        },
        {
          path: 'cigar-production',
          element: withProtectedRoute('conveyorCigarUnitProductions', ConveyorCigarUnitProductions),
        },
        {
          path: 'cigar-unit-box-productions',
          element: withProtectedRoute('cigarUnitBoxProductions', CigarUnitBoxProduction),
        },
        {
          path: 'box-pressed-process',
          element: withProtectedRoute('boxPressedProcess', BoxPressedProcess),
        },
        {
          path: 'workInProgressInventory',
          element: withProtectedRoute('workInProgressInventory', WorkInProgressInventory),
        },
        {
          path: 'work-in-progress-inventory-history',
          element: withProtectedRoute('workInProgressInventory', WorkInProgressInventoryHistory),
        },
        {
          path: 'gift-summary',
          element: withProtectedRoute('giftSummary', GiftSummaryPage),
        },
        {
          path: 'tobacco-production',
          element: withProtectedRoute('tobaccoProduction', TobaccoProduction),
        },
        {
          path: 'tobacco-transfer-summary',
          element: withProtectedRoute('tobaccoTransferSummary', TobaccoTransferSummaryPage),
        },
        {
          path: 'tobacco-galera-production',
          element: withProtectedRoute('tobaccoGaleraProduction', TobaccoGaleraProductionPage),
        },
        {
          path: 'tobacco-humidification-production',
          element: withProtectedRoute('tobaccoHumidificationProduction', TobaccoHumidityRoomProductionPage),
        },
        {
          path: 'tobacco-dry-production',
          element: withProtectedRoute('tobaccoDryProduction', TobaccoDryRoomProductionPage),
        },
        {
          path: 'tobacco-transactions',
          element: withProtectedRoute('tobaccoTransactions', TobaccoTransactionsPage),
        },
        {
          path: 'tobacco-mappings',
          element: withProtectedRoute('tobaccoMappings', TobaccoMappingPage),
        },
        {
          path: 'finished-good-inventory',
          element: withProtectedRoute('finishedGoodInventory', FinishedGoodInventory),
        },
        {
          path: 'rawMaterialInventory',
          element: withProtectedRoute('rawMaterialInventory', RawMaterialInventory),
        },
        {
          path: 'packing-lists',
          element: withProtectedRoute('packingLists', PackingLists),
        },
        {
          path: 'department-order',
          element: withProtectedRoute('departmentOrder', DepartmentOrder),
        },
        {
          path: 'work-orders',
          element: withProtectedRoute('workOrders', WorkOrders),
        },
        {
          path: 'purchase-order',
          element: withProtectedRoute('purchaseOrder', PurchaseOrder),
        },
        {
          path: 'suppliers',
          element: withProtectedRoute('suppliers', Suppliers),
        },
        {
          path: 'scheduler-tasks',
          element: withProtectedRoute('scheduler-tasks', SchedulerTasks),
        },
        {
          path: 'pair-cigar-scheduling',
          element: withProtectedRoute('pairCigarScheduling', PairCigarScheduling),
        },
        {
          path: 'payrolls',
          children: [
            {
              index: true,
              element: (
                <ProtectedRoute path={'payrolls'}>
                  <Suspense fallback={<CircularProgress />}>
                    <Payrolls />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: 'add',
              element: (
                <ProtectedRoute path={'payrolls'}>
                  <Suspense fallback={<CircularProgress />}>
                    <PayrollAddPage />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: 'edit/:id',
              element: (
                <ProtectedRoute path={'payrolls'}>
                  <Suspense fallback={<CircularProgress />}>
                    <PayrollsEdit />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          path: 'invoices',
          element: withProtectedRoute('invoices', Invoices),
        },
        {
          path: 'expenses',
          element: withProtectedRoute('expenses', ExpensesPage),
        },
        {
          path: 'qr-codes',
          element: withProtectedRoute('qrCodes', QrCodes),
        },
        {
          path: 'cigar-units',
          element: withProtectedRoute('cigarUnits', CigarUnitsPage),
        },
        {
          path: 'cigar-units-period-end',
          element: withProtectedRoute('cigarUnitsPeriodEnd', CigarUnitsPeriodEndPage),
        },
        {
          path: 'cigar-unit-productions',
          element: withProtectedRoute('cigarUnitProductions', CigarUnitProduction),
        },
        {
          path: 'tobacco-sales',
          element: withProtectedRoute('tobaccoSales', TobaccoSalesPage),
        },
        {
          path: 'cycle-count',
          element: withProtectedRoute('cycleCount', CycleCountPage),
        },
        {
          path: 'finished-good-templates',
          element: withProtectedRoute('finishedGoodTemplates', FinishedGoodTemplates),
        },
        {
          path: 'payment-code-cigar-mapping',
          element: withProtectedRoute('paymentCodeCigarMapping', PaymentCodeCigarMappingPage),
        },
      ],
    },
    {
      path: 'login',
      element: isAuthentificated ? <Navigate to="/" /> : <Login />,
    },
    // {
    //   path: 'register',
    //   element: <Register />,
    // },
    {
      path: '/',
      element: isAuthentificated ? withSuspense(LogoOnlyLayout) : <Navigate to="/login" />,
      children: [
        { path: '/', element: <Navigate to="/dashboard/app" /> },
        { path: '404', element: <NotFound /> },
        {
          path: 'in-progress',
          element: <PageInProgress />,
        },
        { path: 'access-denied', element: <AccessDenied /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);
}
